import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';

/**
 * Hook for optimistic UI updates
 * Immediately updates UI while async operation completes, rolls back on error
 */
export const useOptimisticUpdate = () => {
    const [savingIds, setSavingIds] = useState(new Set());
    const { enqueueSnackbar } = useSnackbar();

    const executeOptimistic = useCallback(
        async (
            itemId,
            updateLocalState,
            revertLocalState,
            apiCall,
            successMessage = 'Updated successfully',
            errorMessage = 'Failed to update'
        ) => {
            // Add to saving set for UI feedback
            setSavingIds((prev) => new Set(prev).add(itemId));

            try {
                // Optimistically update UI
                updateLocalState();

                // Execute API call
                const result = await apiCall();

                // Success feedback
                enqueueSnackbar(successMessage, { variant: 'success' });
                return result;
            } catch (err) {
                // Revert on error
                revertLocalState();
                const msg = err.response?.data?.message || errorMessage;
                enqueueSnackbar(msg, { variant: 'error' });
                throw err;
            } finally {
                // Remove from saving set
                setSavingIds((prev) => {
                    const next = new Set(prev);
                    next.delete(itemId);
                    return next;
                });
            }
        },
        [enqueueSnackbar]
    );

    return { savingIds, executeOptimistic };
};
